import React, { useEffect, useMemo, useRef, useState } from 'react';
import { App, Typography, Space, Button, Form, Select, Card, Input, Col, Row, InputNumber, Tabs, Divider, TimePicker, Grid, Modal, AutoComplete, Tooltip } from 'antd';
import { Localization, _T } from "../../../../../base-react/src/Localization";
import { EditOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons';
import { apis, hooks, models } from '@vote4dance/judging-sdk';
import { JudgingType, RoundStatus } from '@vote4dance/judging-schema';
import dayjs from "dayjs";
import { RoundPriorityType } from '@vote4dance/judging-types';
import { buildRoundSubmitValidationFromThrough, roundSubmitValidationPreview, safeParseRoundSubmitValidation } from "../../../../../base-react/models/rounds";
var Api = apis.Api;
var ClassRoundSource = models.rounds.ClassRoundSource;
var Stream = hooks.streams.Stream;
var JudgingItem = models.judging.JudgingItem;
var JudgingSource = models.judging.JudgingSource;
var ScheduleSource = models.schedules.ScheduleSource;
var AcroLevelSource = models.judges.AcroLevelSource;
var JudgingPanelSource = models.judges.JudgingPanelSource;
var FloorSource = models.floors.FloorSource;
var ClassItemSource = models.classes.ClassItemSource;
var RoundResultSource = models.results.RoundResultSource;
import { useFederationSettings } from '../../../../../base-react/hooks/Federation';
import { RoundPrioritySettings } from './RoundPrioritySettings';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from '../../../../../base-react/hooks/Competition';
import { useSchedulePages } from '../../../../../base-react/hooks/Schedule';
import { findDifferences, hasInvalidRoundPriorityGroups, sanitizeRoundPriorityGroups, useUnsavedChangesGuard } from '../utilities';
import { ButtonConfirm } from '../../../../../base-react/components/ButtonConfirm';
const { useBreakpoint } = Grid;
const { Text } = Typography;
const timeFormat = 'HH:mm:ss';
export const RoundOverviewSettings = (props) => {
    var _a, _b;
    const { competition_id, class_id, item, multiItems, onCancel, onSave, disabled, onDirtyChange } = props;
    const { notification } = App.useApp();
    const [inputCompetition, event] = useCompetition(competition_id);
    const formRef = useRef(null);
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [showRoundSubmitValidation, setShowRoundSubmitValidation] = useState(false);
    const [showRoundPrioritySettings, setShowRoundPrioritySettings] = useState(false);
    const [classItem, loadingClasses] = Stream(ClassItemSource(competition_id, class_id));
    const [judging, loadingJudging] = Stream(inputCompetition && classItem && JudgingSource(inputCompetition.federation_id, classItem.division_id));
    const [schedule, loadingSchedule] = Stream(event && ScheduleSource(event.competition_id));
    const { pages, pageData } = useSchedulePages(event === null || event === void 0 ? void 0 : event.competition_id, schedule);
    const [floors, loadingFloors] = Stream(event && FloorSource(event.competition_id));
    const [panels, loadingPanels] = Stream(JudgingPanelSource(competition_id));
    const [classRounds, loadingClassRounds] = Stream(ClassRoundSource(class_id));
    const [roundResults] = Stream((item === null || item === void 0 ? void 0 : item.round_id) ? RoundResultSource(item.round_id) : undefined);
    const [hasChanges, setHasChanges] = useState((item === null || item === void 0 ? void 0 : item.round_id) === undefined);
    const screens = useBreakpoint();
    const [acroLevels, loadingAcroLevels] = Stream(AcroLevelSource());
    const { usingAcrobaticLevels } = useFederationSettings(event === null || event === void 0 ? void 0 : event.competition_id);
    const navigate = useNavigate();
    useUnsavedChangesGuard(hasChanges && !!(item === null || item === void 0 ? void 0 : item.round_id));
    const useMultiSelection = multiItems && multiItems.length > 1;
    const editable = useMultiSelection || [RoundStatus.Open, RoundStatus.Ready].includes(item === null || item === void 0 ? void 0 : item.round_status);
    const hasRoundResults = !!roundResults && roundResults.some(result => {
        var _a, _b;
        const ratingValue = (_a = result.result_rating) === null || _a === void 0 ? void 0 : _a.trim();
        const partialValue = (_b = result.result_partial) === null || _b === void 0 ? void 0 : _b.trim();
        const hasRating = ratingValue && ratingValue !== '0';
        const hasPartial = partialValue && partialValue !== '0';
        return hasRating || hasPartial;
    });
    useEffect(() => {
        if (!item || !item.round_id) {
            setTimeout(() => {
                var _a;
                (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }, 50);
        }
    }, []);
    useEffect(() => {
        var _a, _b, _c;
        if (!item || !item.round_id) {
            return;
        }
        const initialValues = getInitialValues();
        const diffValues = findDifferences(initialValues, (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFieldsValue(true));
        delete diffValues['round_skating'];
        if (Object.keys(diffValues).length > 0) {
            (_b = formRef.current) === null || _b === void 0 ? void 0 : _b.resetFields();
            (_c = formRef.current) === null || _c === void 0 ? void 0 : _c.setFieldsValue(initialValues);
            setHasChanges(false);
        }
    }, [item]);
    useEffect(() => {
        onDirtyChange === null || onDirtyChange === void 0 ? void 0 : onDirtyChange(hasChanges);
    }, [hasChanges, onDirtyChange]);
    const setDirty = (dirty) => setHasChanges(dirty);
    useEffect(() => {
        var _a;
        if (judging && judging.length > 0) {
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.validateFields();
        }
    }, [judging]);
    const getInitialValues = () => item && !useMultiSelection ? ({
        ...item,
        round_num_through: item.round_num_through > 0 ? item.round_num_through : null,
        round_heat_length: dayjs(dayjs.duration((item ? item.round_heat_length : 180) * 1000).format(timeFormat), timeFormat),
        round_priority_settings_type: item.round_priority_settings ? JSON.parse(item.round_priority_settings).type : 'disabled',
        round_priority_settings_groups: item.round_priority_settings
            ? sanitizeRoundPriorityGroups(JSON.parse(item.round_priority_settings).groups, { preferMax: true })
            : [],
        round_priority_settings: item.round_priority_settings,
        round_submit_validation: item.round_submit_validation,
        round_submit_validation_text: getRoundSubmitValidationString(item.round_submit_validation),
        schedule_id: item.schedule_id === null ? item.round_id && 'null' : item.schedule_id
    }) : undefined;
    const onFinishFailed = async (props) => {
        notification.error({
            message: props.errorFields[0].errors[0]
        });
    };
    const onFinish = async (fieldsValue) => {
        let updateItems = [];
        const priorityType = fieldsValue['round_priority_settings_type'];
        const priorityGroups = sanitizeRoundPriorityGroups(fieldsValue['round_priority_settings_groups']);
        if (priorityType === RoundPriorityType.Groups && hasInvalidRoundPriorityGroups(priorityGroups)) {
            notification.error({
                message: _T('round.details.round_priority_settings.invalid')
            });
            return;
        }
        // Remove undefined values from fieldsValue
        const cleanedFieldsValue = Object.fromEntries(Object.entries(fieldsValue).filter(([key, value]) => useMultiSelection ? value !== undefined : true));
        for (let attr in cleanedFieldsValue) {
            switch (attr) {
                case 'round_num_through':
                    {
                        cleanedFieldsValue[attr] = fieldsValue[attr] != null ? fieldsValue[attr] : 0;
                    }
                    break;
                case 'round_heat_length':
                    {
                        const timeSeconds = dayjs.duration({ minutes: fieldsValue['round_heat_length'].minute(), seconds: fieldsValue['round_heat_length'].second() }).asSeconds();
                        cleanedFieldsValue[attr] = timeSeconds;
                    }
                    break;
                case 'acro_id':
                    {
                        cleanedFieldsValue[attr] = fieldsValue['acro_id'] != undefined ? fieldsValue['acro_id'] : null;
                    }
                    break;
                case 'schedule_id':
                    {
                        cleanedFieldsValue[attr] = [undefined, 'null'].includes(fieldsValue['schedule_id']) ? null : fieldsValue['schedule_id'];
                    }
                    break;
                case 'round_priority_settings':
                case 'round_priority_settings_type':
                case 'round_priority_settings_groups':
                    {
                        cleanedFieldsValue['round_priority_settings'] = priorityType === 'disabled' ? null : JSON.stringify(priorityType === RoundPriorityType.Groups ? { type: priorityType, groups: priorityGroups } : { type: priorityType });
                    }
                    break;
            }
        }
        if (useMultiSelection) {
            // Assuming allItems is an array of objects
            updateItems = multiItems
                .map((a) => ({
                ...a, // Keep all previous attributes
                ...cleanedFieldsValue, // Update the fields that are not undefined
            }));
        }
        else {
            updateItems = [
                {
                    ...item, // Keep all previous attributes
                    ...cleanedFieldsValue,
                    round_label: fieldsValue['round_label'] ? fieldsValue['round_label'] : _T('common.empty'),
                },
            ];
        }
        let createdResult;
        const updatePromises = updateItems.map(async (updateItem) => {
            const itemToUpdate = updateItem;
            let action;
            if (itemToUpdate.round_id) {
                action = Api.update(`${ClassRoundSource(itemToUpdate.class_id).url}/${itemToUpdate.round_id}`, updateItem);
            }
            else {
                action = Api.create(ClassRoundSource(itemToUpdate.class_id).url, updateItem).then((result) => {
                    createdResult = result;
                    if (itemToUpdate.round_count > 0 && parseInt(itemToUpdate.round_parent_id) > 0 && itemToUpdate.judging_type != JudgingType.Match) {
                        return Api.call(`${ClassRoundSource(itemToUpdate.class_id).url}/${itemToUpdate.round_parent_id}`, 'copy-teams', { target: result.round_id });
                    }
                    return;
                });
            }
            return action;
        });
        try {
            await Promise.all(updatePromises);
            setLoading(false);
            handleCancel(null, true);
        }
        catch (err) {
            setLoading(false);
            notification.error({
                message: err.message,
            });
        }
        if (onSave) {
            onSave();
        }
        if (createdResult) {
            navigate(`../classes/${createdResult.class_id}/${createdResult.round_id}`);
        }
    };
    const handleValuesChange = (changedValues, allValues) => {
        setDirty(true);
        if (Object.prototype.hasOwnProperty.call(changedValues, 'round_num_through')) {
            updateRoundSubmitValidationFromThrough(changedValues.round_num_through, allValues.judging_id);
        }
        if (Object.prototype.hasOwnProperty.call(changedValues, 'judging_id')) {
            updateRoundSubmitValidationFromThrough(allValues.round_num_through, changedValues.judging_id);
        }
    };
    const handleCancel = (e, skipReset) => {
        var _a;
        if (!skipReset) {
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.setFieldsValue(getInitialValues());
        }
        setDirty(false);
        if (onCancel) {
            onCancel();
        }
    };
    const onPressToggle = (name) => {
        var _a, _b;
        const currentValue = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFieldValue(name);
        var newValue = currentValue != null ? null : 1;
        (_b = formRef.current) === null || _b === void 0 ? void 0 : _b.setFieldValue(name, newValue);
        setDirty(true);
    };
    const getItemJudging = (judging_id) => judging && judging.find((a) => a.judging_id == judging_id);
    const getUsingRoundSubmitValidation = (judging_id) => {
        const itemJudging = judging && Array.isArray(judging) && judging.find((a) => a.judging_id == judging_id);
        const judgingSettings = itemJudging && JudgingItem.getJudgingSettings(itemJudging);
        const usingRoundSubmitValidation = judgingSettings ? judgingSettings.validation == 'absolute' : false;
        return usingRoundSubmitValidation;
    };
    const updateRoundSubmitValidationFromThrough = (roundNumThrough, judging_id) => {
        var _a, _b;
        if (roundNumThrough == null || Number.isNaN(roundNumThrough) || !judging_id) {
            return;
        }
        const itemJudging = getItemJudging(judging_id);
        const judgingSettings = itemJudging && JudgingItem.getJudgingSettings(itemJudging);
        const currentValidation = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFieldValue('round_submit_validation');
        const validationString = buildRoundSubmitValidationFromThrough(roundNumThrough, judgingSettings, currentValidation);
        if (!validationString)
            return;
        (_b = formRef.current) === null || _b === void 0 ? void 0 : _b.setFieldsValue({
            round_submit_validation: validationString,
            round_submit_validation_text: roundSubmitValidationPreview(validationString)
        });
    };
    const getRoundSubmitValidationString = (validation) => roundSubmitValidationPreview(validation);
    const childRounds = useMemo(() => {
        if (!(item === null || item === void 0 ? void 0 : item.round_id) || !classRounds) {
            return [];
        }
        return classRounds.filter((roundItem) => String(roundItem.round_parent_id) === String(item.round_id));
    }, [classRounds, item === null || item === void 0 ? void 0 : item.round_id]);
    const hasChildRounds = useMemo(() => { var _a; return Number((_a = item === null || item === void 0 ? void 0 : item.round_children) !== null && _a !== void 0 ? _a : 0) > 0; }, [item === null || item === void 0 ? void 0 : item.round_children]);
    const applyHeatsToChildRounds = async (done) => {
        var _a, _b, _c;
        const heatValue = (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFieldValue('round_num_heats')) !== null && _b !== void 0 ? _b : item === null || item === void 0 ? void 0 : item.round_num_heats;
        const heatCount = Number(heatValue);
        if (!heatValue || Number.isNaN(heatCount) || heatCount <= 0) {
            notification.warning({
                message: _T('round.details.round_num_heats.rule')
            });
            done();
            return;
        }
        if (!childRounds.length) {
            notification.warning({
                message: _T('common.error')
            });
            done();
            return;
        }
        try {
            const classRoundUrl = ClassRoundSource(class_id).url;
            await Promise.all(childRounds.map((childRound) => {
                const roundPayload = {
                    ...childRound,
                    round_num_heats: heatCount,
                };
                return Api.update(`${classRoundUrl}/${childRound.round_id}`, roundPayload);
            }));
            notification.success({
                message: _T('common.apply.success', { name: _T('round.details.round_num_heats.label').toLowerCase() })
            });
        }
        catch (err) {
            notification.error({
                message: (_c = err === null || err === void 0 ? void 0 : err.message) !== null && _c !== void 0 ? _c : _T('common.error')
            });
        }
        finally {
            done();
        }
    };
    const parentJudgings = judging && Array.isArray(judging) ? judging
        .reduce((acc, current) => {
        if (!acc.some(item => _T('judging-parent.' + item.judging_type) === _T('judging-parent.' + current.judging_type))) {
            acc.push(current);
        }
        return acc;
    }, []) : [];
    let validJudgings = judging && (hasChildRounds ? parentJudgings : judging)
        .filter((a) => a.judging_state === 'active' || a.judging_id === (item === null || item === void 0 ? void 0 : item.judging_id));
    if (item && !(validJudgings === null || validJudgings === void 0 ? void 0 : validJudgings.map(a => a.judging_id).includes(item.judging_id))) {
        validJudgings && validJudgings.push({
            judging_id: item.judging_id,
            judging_type: item.judging_type,
            judging_label: item.judging_label,
            judging_state: 'draft'
        });
    }
    ;
    const scheduleOptions = pages && pages.length > 1 ? pages.map((page, index) => ({
        label: page.schedule_value,
        value: page.schedule_id,
        options: pageData[index].map(data => ({
            label: data.schedule_value,
            value: data.schedule_id
        }))
    })) : pageData[0].map(data => ({
        label: data.schedule_value,
        value: data.schedule_id
    }));
    const localizedSuggestions = _T('round.details.round_label.suggestions');
    const options = localizedSuggestions.split(',').map(a => ({ value: a.trim() }));
    const panelOptions = (panels !== null && panels !== void 0 ? panels : [])
        .filter((panel) => panel.panel_id)
        .map((panel) => ({
        label: panel.panel_name || panel.panel_id,
        value: panel.panel_id
    }));
    const defaultPanelId = classItem === null || classItem === void 0 ? void 0 : classItem.default_panel_id;
    const defaultPanelPlaceholder = defaultPanelId != null
        ? (_b = (_a = panels === null || panels === void 0 ? void 0 : panels.find((panel) => String(panel.panel_id) === String(defaultPanelId))) === null || _a === void 0 ? void 0 : _a.panel_name) !== null && _b !== void 0 ? _b : defaultPanelId
        : undefined;
    const deleteWarningText = _T('common.delete.title', { name: _T('round.results').toLowerCase() });
    const deleteTooltipTitle = (React.createElement(Space, { align: "center", size: 4 },
        React.createElement(StopOutlined, null),
        React.createElement("span", null, deleteWarningText)));
    const labelWithLockTooltip = (label) => hasRoundResults ? (React.createElement(Tooltip, { placement: "top", title: deleteTooltipTitle }, label)) : label;
    const panelExtra = _T('panel.extra');
    const sections = [
        React.createElement(Row, { gutter: [12, 12] },
            React.createElement(Col, { style: { width: '100%' } },
                React.createElement(Form.Item, { name: "round_label", label: _T('round.details.round_label.label'), rules: [
                        {
                            validator: (_, value) => {
                                if (!value || !value.startsWith(' ')) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error(_T('round.details.round_label.empty')));
                            }
                        },
                    ] },
                    React.createElement(AutoComplete, { ref: inputRef, options: options, filterOption: (inputValue, option) => option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1, placeholder: _T('round.details.round_label.placeholder'), allowClear: true, style: { width: '100%' } })),
                React.createElement(Form.Item, { name: "judging_id", label: labelWithLockTooltip(_T('round.details.judging_id.label')), rules: [
                        {
                            validator: (_, value) => {
                                const itemJudging = getItemJudging(value);
                                const invalidJudging = !itemJudging || (itemJudging && itemJudging.judging_state != 'active');
                                return useMultiSelection || !invalidJudging ? Promise.resolve() : Promise.reject(new Error(_T('judging.unsupported')));
                            }
                        },
                    ] },
                    React.createElement(Select, { loading: loadingJudging || loadingClasses, options: validJudgings === null || validJudgings === void 0 ? void 0 : validJudgings.map((judging) => ({
                            value: judging.judging_id,
                            label: item && item.round_children > 0 ? _T('judging-parent.' + judging.judging_type) : judging.judging_label && Localization.getLocalizedString(JSON.parse(judging.judging_label))
                        })), disabled: !editable || disabled || hasRoundResults })),
                usingAcrobaticLevels &&
                    React.createElement(Form.Item, { label: _T('team.details.acro_id.label'), name: "acro_id" },
                        React.createElement(Select, { allowClear: true, placeholder: _T('team.details.acro_id.placeholder'), loading: loadingAcroLevels }, acroLevels && acroLevels.map((acro, index) => (React.createElement(Select.Option, { value: acro.acro_id, key: index }, acro.acro_name))))),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_num_through !== currentValues.round_num_through }, ({ getFieldValue }) => React.createElement(Form.Item, { label: _T('round.details.round_num_through.label'), extra: _T('round.details.round_num_through.extra') },
                    React.createElement(Space.Compact, { style: { width: '100%' } },
                        React.createElement(Form.Item, { name: "round_num_through", noStyle: true },
                            React.createElement(InputNumber, { placeholder: _T('common.disabled'), min: 1, style: { width: '100%' } })),
                        React.createElement(Button, { onClick: () => onPressToggle('round_num_through') }, _T(getFieldValue('round_num_through') == null ? 'common.enable' : 'common.disable.title'))))),
                React.createElement(Form.Item, { name: "round_num_heats", label: _T('round.details.round_num_heats.label'), extra: !useMultiSelection && hasChildRounds &&
                        (React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_num_heats !== currentValues.round_num_heats }, ({ getFieldValue }) => {
                            var _a;
                            const heatValue = (_a = getFieldValue('round_num_heats')) !== null && _a !== void 0 ? _a : item === null || item === void 0 ? void 0 : item.round_num_heats;
                            const heatCount = Number(heatValue);
                            const hasValidHeat = heatValue != null && !Number.isNaN(heatCount) && heatCount > 0;
                            const applyDisabled = !hasValidHeat || !childRounds.length || !editable || disabled || loadingClassRounds;
                            return (React.createElement(ButtonConfirm, { title: _T('common.confirm'), description: _T('round.details.round_num_heats.applyChildRounds'), onConfirm: applyHeatsToChildRounds, buttonProps: {
                                    type: 'link',
                                    size: 'small',
                                    label: _T('round.details.round_num_heats.applyChildRounds'),
                                    disabled: applyDisabled,
                                    style: { paddingInlineStart: 0 },
                                } }));
                        })) },
                    React.createElement(InputNumber, { min: 1, style: { width: '100%' } })),
                React.createElement(Form.Item, { name: "schedule_id", label: _T('round.details.schedule_id.label'), rules: [{ required: !useMultiSelection, message: _T('round.details.schedule_id.rule') }] },
                    React.createElement(Select, { loading: loadingSchedule, allowClear: useMultiSelection, options: scheduleOptions })),
                React.createElement(Form.Item, { name: "round_beat", label: _T('round.details.round_beat.label') },
                    React.createElement(Input, null)))),
        React.createElement(Row, { gutter: [12, 12] },
            React.createElement(Col, { style: { width: '100%' } },
                React.createElement(Form.Item, { name: "round_heat_length", rules: [{ required: !useMultiSelection, message: _T('round.details.round_heat_length.rule') }], label: _T('round.details.round_heat_length.label'), extra: _T('round.details.round_heat_length.extra') },
                    React.createElement(TimePicker, { secondStep: 5, showNow: false, format: 'mm:ss', style: { width: '100%' }, allowClear: useMultiSelection })),
                React.createElement(Form.Item, { name: "round_heat_lock", label: _T('round.details.round_heat_lock.label'), extra: _T('round.details.round_heat_lock.extra') },
                    React.createElement(Select, { allowClear: useMultiSelection },
                        React.createElement(Select.Option, { value: 0, key: 0 }, _T('common.disabled')),
                        React.createElement(Select.Option, { value: 1, key: 1 }, _T('common.enable')))),
                React.createElement(Form.Item, { name: "floor_id", label: _T('round.details.floor_id.label') },
                    React.createElement(Select, { loading: loadingFloors, allowClear: useMultiSelection }, floors && floors.map((floorItem, index) => (React.createElement(Select.Option, { value: floorItem.floor_id, key: index }, floorItem.floor_name))))),
                React.createElement(Form.Item, { name: "round_comment", label: _T('round.details.round_comment.label') },
                    React.createElement(Input, { allowClear: true, placeholder: _T('round.details.round_comment.placeholder') })))),
        React.createElement(Row, { gutter: [12, 12] },
            React.createElement(Col, { style: { width: '100%' } },
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_position !== currentValues.round_position }, ({ getFieldValue }) => React.createElement(Form.Item, { label: _T('round.details.round_position.label'), extra: _T('round.details.round_position.extra') },
                    React.createElement(Space.Compact, { style: { width: '100%' } },
                        React.createElement(Form.Item, { name: "round_position", noStyle: true },
                            React.createElement(InputNumber, { placeholder: _T('common.disabled'), min: 1, style: { width: '100%' } })),
                        React.createElement(Button, { onClick: () => onPressToggle('round_position') }, _T(getFieldValue('round_position') == null ? 'common.enable' : 'common.disable.title'))))),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_hide_results_pos !== currentValues.round_hide_results_pos }, ({ getFieldValue }) => React.createElement(Form.Item, { label: _T('round.details.round_hide_results_pos.label'), extra: _T('round.details.round_hide_results_pos.extra') },
                    React.createElement(Space.Compact, { style: { width: '100%' } },
                        React.createElement(Form.Item, { name: "round_hide_results_pos", noStyle: true },
                            React.createElement(InputNumber, { placeholder: _T('common.disabled'), min: 0, style: { width: '100%' } })),
                        React.createElement(Button, { onClick: () => onPressToggle('round_hide_results_pos') }, _T(getFieldValue('round_hide_results_pos') == null ? 'common.enable' : 'common.disable.title'))))),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_hide_results_pos !== currentValues.round_hide_results_pos }, ({ getFieldValue }) => React.createElement(Form.Item, { name: "round_hide_results_type", label: _T('round.details.round_hide_results_type.label') },
                    React.createElement(Select, { disabled: !editable || getFieldValue('round_hide_results_pos') == null, allowClear: useMultiSelection }, ['marks', 'placements', 'partial', 'coach', 'all'].map((value, index) => (React.createElement(Select.Option, { value: value, key: index }, _T('round.details.round_hide_results_type.' + value))))))),
                React.createElement(Form.Item, { name: "round_qualifier", label: _T('round.details.round_qualifier.label'), extra: _T('round.details.round_qualifier.extra') },
                    React.createElement(Select, { allowClear: useMultiSelection },
                        React.createElement(Select.Option, { value: 0, key: 0 }, _T('common.disabled')),
                        React.createElement(Select.Option, { value: 1, key: 1 }, _T('common.enable')))),
                React.createElement(Form.Item, { name: "panel_id", label: labelWithLockTooltip(_T('panel.title')), extra: panelExtra, getValueFromEvent: (value) => value !== null && value !== void 0 ? value : null },
                    React.createElement(Select, { allowClear: true, loading: loadingPanels, placeholder: defaultPanelPlaceholder, options: panelOptions, disabled: !editable || disabled || hasRoundResults })))),
        React.createElement(Row, { gutter: [12, 12] },
            React.createElement(Col, { style: { width: '100%' } },
                React.createElement(Form.Item, { noStyle: true, name: 'round_priority_settings' },
                    React.createElement(Input, { type: "hidden" })),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.round_priority_settings_type !== currentValues.round_priority_settings_type ||
                        prevValues.round_priority_settings_groups !== currentValues.round_priority_settings_groups }, ({ getFieldValue, setFieldValue }) => React.createElement(React.Fragment, null,
                    React.createElement(Form.Item, { label: _T('round.details.round_priority_settings.label'), extra: _T('round.details.round_priority_settings.extra') },
                        React.createElement(Space.Compact, { block: true },
                            React.createElement(Form.Item, { name: 'round_priority_settings_type', noStyle: true },
                                React.createElement(Select, { onChange: value => {
                                        setFieldValue('round_priority_settings_type', value);
                                        setDirty(true);
                                    }, allowClear: useMultiSelection }, ['disabled', RoundPriorityType.Alternates, RoundPriorityType.Groups].map((value, index) => (React.createElement(Select.Option, { value: value, key: index }, _T('round.details.round_priority_settings.type.' + value)))))),
                            getFieldValue('round_priority_settings_type') === RoundPriorityType.Groups &&
                                React.createElement(Button, { onClick: () => setShowRoundPrioritySettings(true) }, _T(getFieldValue('round_priority_settings') == null ? 'common.enable' : 'common.edit')))),
                    item &&
                        React.createElement(Form.Item, { noStyle: true, name: 'round_priority_settings_groups' },
                            React.createElement(RoundPrioritySettings, { item: item, list: getFieldValue('round_priority_settings_groups'), open: showRoundPrioritySettings, disabled: !editable, onClose: () => setShowRoundPrioritySettings(false), onValue: value => {
                                    const sanitizedGroups = sanitizeRoundPriorityGroups(value);
                                    setFieldValue('round_priority_settings_groups', sanitizedGroups);
                                    const typeValue = getFieldValue('round_priority_settings_type');
                                    const priorityValue = typeValue === 'disabled' ? null : JSON.stringify(typeValue === RoundPriorityType.Groups ? { type: typeValue, groups: sanitizedGroups } : { type: typeValue });
                                    setFieldValue('round_priority_settings', priorityValue);
                                    setDirty(true);
                                } })))),
                React.createElement(Form.Item, { noStyle: true, name: 'round_submit_validation' },
                    React.createElement(Input, { type: "hidden" })),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.judging_id !== currentValues.judging_id ||
                        prevValues.round_submit_validation_text !== currentValues.round_submit_validation_text ||
                        prevValues.round_submit_validation !== currentValues.round_submit_validation }, ({ getFieldValue, setFieldValue }) => {
                    var _a;
                    return React.createElement(React.Fragment, null,
                        React.createElement(Form.Item, { label: _T('round.details.round_submit_validation.label'), extra: _T('round.details.round_submit_validation.extra'), hidden: !getUsingRoundSubmitValidation(getFieldValue('judging_id')) },
                            React.createElement(Space.Compact, { style: { width: '100%' } },
                                React.createElement(Input, { placeholder: _T('common.disabled'), style: { width: '100%' }, disabled: true, allowClear: useMultiSelection, value: (_a = getFieldValue('round_submit_validation_text')) !== null && _a !== void 0 ? _a : '' }),
                                React.createElement(Button, { disabled: !editable, onClick: () => setShowRoundSubmitValidation(true) }, _T(getFieldValue('round_submit_validation') == null ? 'common.enable' : 'common.edit')))),
                        React.createElement(RoundSubmitValidation, { validation: getFieldValue('round_submit_validation'), judging: getItemJudging(getFieldValue('judging_id')), open: showRoundSubmitValidation, onClose: () => setShowRoundSubmitValidation(false), onValue: value => {
                                setFieldValue('round_submit_validation', value);
                                setFieldValue('round_submit_validation_text', getRoundSubmitValidationString(value));
                                setDirty(true);
                            } }));
                }),
                React.createElement(Form.Item, { name: "round_weight", label: _T('round.details.round_weight.label'), extra: _T('round.details.round_weight.extra') },
                    React.createElement(InputNumber, { min: 0, style: { width: '100%' } })),
                React.createElement(Form.Item, { noStyle: true, shouldUpdate: (prevValues, currentValues) => prevValues.judging_id !== currentValues.judging_id }, ({ getFieldValue }) => {
                    var _a;
                    return React.createElement(Form.Item, { name: "round_tolerance", label: _T('round.details.round_tolerance.label'), extra: _T('round.details.round_tolerance.extra'), hidden: ((_a = getItemJudging(getFieldValue('judging_id'))) === null || _a === void 0 ? void 0 : _a.judging_type) !== JudgingType.Ajs },
                        React.createElement(InputNumber, { min: 0, style: { width: '100%' } }));
                })))
    ];
    return (React.createElement(Card, { title: React.createElement(React.Fragment, null,
            React.createElement(EditOutlined, null),
            " ",
            _T('round.details.title')), style: { height: '100%' } },
        React.createElement(Form, { ref: formRef, name: "round_overview_settings", disabled: !editable || loading || disabled, onFinish: onFinish, onFinishFailed: onFinishFailed, onValuesChange: handleValuesChange, initialValues: getInitialValues(), labelWrap: true, labelCol: { span: 9 }, wrapperCol: { span: 15 } },
            React.createElement(Tabs, { tabPlacement: screens.md ? 'start' : 'top', items: ['basic', 'schedule', 'results', 'priority'].map((section, index) => ({
                    label: _T('round.details.' + section + '.label'),
                    key: section,
                    children: sections[index],
                    forceRender: true
                })) }),
            React.createElement(Divider, null),
            editable ?
                React.createElement(Space, null,
                    React.createElement(Button, { onClick: handleCancel, disabled: !hasChanges }, _T('common.cancel.title')),
                    React.createElement(Button, { type: "primary", htmlType: "submit", icon: React.createElement(SaveOutlined, null), disabled: !hasChanges }, _T('common.save')))
                :
                    React.createElement(Text, null, _T('round.openToEdit')))));
};
export const RoundSubmitValidation = (props) => {
    const { validation, judging, open, onClose, onValue } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const settings = judging && JudgingItem.getJudgingSettings(judging);
    useEffect(() => {
        const parsed = safeParseRoundSubmitValidation(validation);
        if (parsed === null || parsed === void 0 ? void 0 : parsed.absolute) {
            form.setFieldsValue(parsed.absolute);
        }
    }, [validation]);
    const onFinish = (fieldsValue) => {
        switch (settings === null || settings === void 0 ? void 0 : settings.validation) {
            case 'absolute':
                {
                    const entries = settings.entries;
                    const absolute = {};
                    for (const entry of entries) {
                        const value = fieldsValue[entry];
                        absolute[entry] = value ? value : 0;
                    }
                    onValue(JSON.stringify({
                        absolute
                    }));
                    onClose();
                }
                break;
        }
    };
    const disable = () => {
        form.resetFields();
        onValue(null);
        onClose();
    };
    return (React.createElement(Modal, { title: _T('round.details.round_submit_validation.label'), open: open, forceRender: true, okButtonProps: { form: 'round_submit_validation', htmlType: 'submit' }, okText: _T('common.ok'), cancelText: _T('common.cancel.title'), confirmLoading: isLoading, onCancel: onClose },
        React.createElement(Form, { name: 'round_submit_validation', form: form, disabled: isLoading, onFinish: onFinish },
            settings && settings.entries && settings.entries.map((entry, index) => React.createElement(Form.Item, { name: entry, label: entry, key: index },
                React.createElement(InputNumber, { min: 0 }))),
            React.createElement(Button, { type: "dashed", onClick: disable }, _T('common.disable.title')))));
};
export const RoundMultiEditSettings = (props) => {
    const { items, disabled, buttonProps } = props;
    const [visible, setVisible] = useState(false);
    const openModal = () => {
        setVisible(true);
    };
    const closeModal = () => {
        setVisible(false);
    };
    const disableEdit = disabled || items.length === 0 || !items.every(a => a.round_status === RoundStatus.Open);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { icon: React.createElement(EditOutlined, null), disabled: disableEdit, onClick: openModal, ...buttonProps }, _T('common.edit')),
        React.createElement(Modal, { title: _T('common.details', { name: _T('round.title') }), open: visible, onCancel: closeModal, destroyOnHidden: true, width: 800, footer: null }, items && items.length > 0 && React.createElement(RoundOverviewSettings, { competition_id: items[0].competition_id, class_id: items[0].class_id, item: items.length === 1 ? items[0] : undefined, multiItems: items, disabled: disableEdit, onCancel: closeModal, onSave: closeModal }))));
};
